/**
 * File utility functions
 * Common helpers for file operations across the application
 */

/**
 * Recursively traverse a file tree and collect all files
 * @param {FileSystemEntry} item - File system entry (from drag and drop)
 * @param {string} path - Current path
 * @param {Array} files - Array to collect files
 * @returns {Promise<void>}
 */
export const traverseFileTree = async (item, path, files) => {
  return new Promise((resolve) => {
    if (item.isFile) {
      item.file((file) => {
        // Set both relativePath and webkitRelativePath as non-writable properties
        // to maintain compatibility with the File API
        Object.defineProperty(file, 'relativePath', {
          value: path + file.name,
          writable: false
        });
        Object.defineProperty(file, 'webkitRelativePath', {
          value: path + file.name,
          writable: false
        });
        files.push(file);
        resolve();
      });
    } else if (item.isDirectory) {
      const dirReader = item.createReader();
      const readEntries = () => {
        dirReader.readEntries(async (entries) => {
          if (entries.length === 0) {
            resolve();
          } else {
            for (let i = 0; i < entries.length; i++) {
              await traverseFileTree(entries[i], path + item.name + '/', files);
            }
            readEntries(); // Continue reading if there are more entries
          }
        });
      };
      readEntries();
    }
  });
};

/**
 * Get file extension
 * @param {string} filename - File name
 * @returns {string} - File extension without dot
 */
export const getFileExtension = (filename) => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

/**
 * Check if file is a PDB file
 * @param {string} filename - File name
 * @returns {boolean}
 */
export const isPDBFile = (filename) => {
  const ext = getFileExtension(filename);
  return ext === 'pdb';
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Extract directory path from file path
 * @param {string} filePath - Full file path
 * @returns {string} - Directory path
 */
export const getDirectoryPath = (filePath) => {
  const parts = filePath.split('/');
  parts.pop(); // Remove filename
  return parts.join('/');
};

/**
 * Extract filename from path
 * @param {string} filePath - Full file path
 * @returns {string} - Filename
 */
export const getFilename = (filePath) => {
  const parts = filePath.split('/');
  return parts[parts.length - 1];
};

/**
 * Sort files by path
 * @param {Array} files - Array of file objects
 * @returns {Array} - Sorted array
 */
export const sortFilesByPath = (files) => {
  return [...files].sort((a, b) => {
    const pathA = a.relativePath || a.name;
    const pathB = b.relativePath || b.name;
    return pathA.localeCompare(pathB);
  });
};